import { useMemo } from 'react';
import { WidgetCard } from './WidgetCard';
import { RiskScoreBar } from './RiskScoreBar';
import { MOCK_CUSTOMERS, filterCustomersByDateRange } from '../mocks/customers';
import type { DateRange } from '../types';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const statusStyles: Record<string, string> = {
  active: 'bg-red-100 text-red-700',
  monitoring: 'bg-amber-100 text-amber-700',
  cleared: 'bg-green-100 text-green-700',
};

interface FlaggedCustomersWidgetProps {
  dateRange: DateRange;
}

export function FlaggedCustomersWidget({ dateRange }: FlaggedCustomersWidgetProps) {
  const filtered = useMemo(
    () => filterCustomersByDateRange(MOCK_CUSTOMERS, dateRange.start, dateRange.end),
    [dateRange]
  );

  return (
    <WidgetCard
      title="Flagged Customers"
      action={
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
          {filtered.length}
        </span>
      }
    >
      <div className="max-h-80 overflow-auto rounded-lg border border-gray-100">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 bg-gray-50">
            <tr>
              <th className="px-3 py-2 font-medium text-gray-700">Customer</th>
              <th className="px-3 py-2 font-medium text-gray-700">Risk</th>
              <th className="hidden px-3 py-2 font-medium text-gray-700 sm:table-cell">Reason</th>
              <th className="hidden px-3 py-2 font-medium text-gray-700 lg:table-cell">Flagged</th>
              <th className="px-3 py-2 font-medium text-gray-700">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((customer) => (
              <tr key={customer.id} className="hover:bg-gray-50">
                <td className="px-3 py-3">
                  <div className="font-medium text-gray-900">{customer.name}</div>
                  <div className="mt-0.5 text-xs text-gray-500 sm:hidden">{customer.reason}</div>
                </td>
                <td className="px-3 py-3">
                  <RiskScoreBar score={customer.riskScore} />
                </td>
                <td className="hidden max-w-xs px-3 py-3 text-gray-600 sm:table-cell">
                  {customer.reason}
                </td>
                <td className="hidden whitespace-nowrap px-3 py-3 text-gray-500 lg:table-cell">
                  {formatDate(customer.flaggedAt)}
                </td>
                <td className="px-3 py-3">
                  <span
                    className={[
                      'inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize',
                      statusStyles[customer.status] ?? 'bg-gray-100 text-gray-700',
                    ].join(' ')}
                  >
                    {customer.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-500">
            No customers match the selected date range.
          </div>
        )}
      </div>
    </WidgetCard>
  );
}
